import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Plus, Stamp, X } from 'lucide-react';
import { fitSenderSignature, type AnchorRole } from '../lib/anchors.js';
import { cn } from '../lib/cn.js';
import {
  forgetSignature,
  rememberSignature,
  SAVED_SIGNATURES_MAX,
  savedSignatures,
  type SignatureStore,
} from '../lib/savedSignature.js';
import { useSession } from '../lib/session.js';
import { inkOnly } from '../lib/signatureImage.js';
import { signingImage } from '../pages/signing/SignatureAssets.js';
import { AnchoredMenu } from './AnchoredMenu.js';
import { Modal } from './Modal.js';
import { SignaturePad } from './SignaturePad.js';
import { useToast } from './Toast.js';
import { ErrorNotice, Button, IconButton } from './ui.js';

/**
 * 1.1 — what goes in a place on the document, and the sender's own signature.
 *
 * Nitzan, 17.09, on v19: a dialog asking what goes in a place before there is
 * anything on the page is strange. The box goes down first, with its bar under
 * it, and the bar has a signature button and a text button, each with a short
 * menu: yourself, or the recipient. Signing yourself opens the signature dialog,
 * where the sender draws, uploads a picture of a signature, or picks one of the
 * signatures kept on this device.
 *
 * The dialog is open for as long as it is mounted: the placer mounts it only
 * while it is asking. For the pad that is a requirement, not a convenience. It
 * sizes its canvas from its own box as it mounts, and a pad mounted anywhere it
 * is not yet laid out keeps a 300x150 canvas, with every stroke landing away
 * from the finger that drew it.
 */

/** Each menu's options, yourself first and then the recipient, as the menu was asked for. */
const MENU_OPTIONS: Record<'signature' | 'text', Array<{ role: AnchorRole; label: string }>> = {
  signature: [
    { role: 'sender-signature', label: 'signing.placeSignSelf' },
    { role: 'signer-signature', label: 'signing.placeSignByRecipient' },
  ],
  text: [
    { role: 'sender-text', label: 'signing.placeFillSelf' },
    { role: 'signer-text', label: 'signing.placeFillByRecipient' },
  ],
};

/**
 * The options under a box's signature button or its text button, with what the
 * box is now checked. A signature box asked about text is neither kind of text
 * yet, so nothing in that menu is checked.
 */
export function PlaceRoleOptions({
  menu,
  current,
  onChoose,
}: {
  menu: 'signature' | 'text';
  current: AnchorRole;
  onChoose: (role: AnchorRole) => void;
}) {
  const { t } = useTranslation();

  return (
    <div role="menu" className="p-1">
      {MENU_OPTIONS[menu].map(({ role, label }) => {
        const chosen = role === current;
        return (
          <button
            key={role}
            type="button"
            role="menuitemradio"
            aria-checked={chosen}
            onClick={() => onChoose(role)}
            className={cn(
              'w-full min-h-11 rounded-lg px-3 py-2 flex items-center gap-2 text-start text-sm transition-colors',
              chosen ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted',
            )}
          >
            <Check className={cn('h-4 w-4 shrink-0', !chosen && 'invisible')} />
            {t(label)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Fit both dimensions: a portrait photo can already be narrower than every
 * attempted width while still encoding thousands of rows of paper grain.
 */
function fitSignature(source: CanvasImageSource, width: number, height: number): string | null {
  return fitSenderSignature((maxWidth) => {
    const scale = Math.min(1, maxWidth / Math.max(width, height));
    const canvas = window.document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d context');
    // A pad drawn on a phone is shrunk to a third or less, and a line a few
    // pixels wide falls between the pixels the cheapest filter reads: it comes
    // out broken.
    context.imageSmoothingQuality = 'high';
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  });
}

function loadPicture(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

/** The pad's picture, made small enough to keep. */
async function shrinkDrawing(drawn: string): Promise<string | null> {
  const picture = await loadPicture(drawn);
  return fitSignature(picture, picture.naturalWidth, picture.naturalHeight);
}

/** Compose both independent marks, then respect the sender anchor/storage budget. */
export async function senderSigningImage(signature: string | null, stamp: string | null): Promise<string | null> {
  const composed = await signingImage(signature, stamp);
  return composed ? shrinkDrawing(composed) : null;
}

/** The longest side a picture is read at. A phone's photo is several times this, and the ink needs none of it. */
const PICTURE_SIDE = 1600;

/** The largest picture file taken: decoding a much bigger one can take a phone's tab down with it. */
const PICTURE_BYTES = 10 * 1024 * 1024;

/** Why a picture gave no signature, carrying the key that says so. */
class PictureError extends Error {}

/**
 * The signature in a picture of one: its paper taken out, cut down to the ink,
 * and made small enough to keep. Throws a `PictureError` when the picture is
 * not one to take or has no signature in it.
 */
export async function signatureFromPicture(file: File, colorMode: 'uniform' | 'original' | 'black-white' = 'uniform'): Promise<string> {
  if (file.type !== 'image/png' && file.type !== 'image/jpeg') throw new PictureError('signing.senderSignatureUnreadable');
  if (file.size > PICTURE_BYTES) throw new PictureError('validation.signature.fileTooLarge');

  // A `blob:` address, as the signer's own upload reads a picture: the page's
  // policy lets images load from one.
  const address = URL.createObjectURL(file);
  let picture: HTMLImageElement;
  try {
    picture = await loadPicture(address);
  } catch {
    throw new PictureError('signing.senderSignatureUnreadable');
  } finally {
    URL.revokeObjectURL(address);
  }

  const scale = Math.min(1, PICTURE_SIDE / Math.max(picture.naturalWidth, picture.naturalHeight));
  const width = Math.max(1, Math.round(picture.naturalWidth * scale));
  const height = Math.max(1, Math.round(picture.naturalHeight * scale));
  const reading = window.document.createElement('canvas');
  reading.width = width;
  reading.height = height;
  const read = reading.getContext('2d', { willReadFrequently: true });
  if (!read) throw new Error('no 2d context');
  read.imageSmoothingQuality = 'high';
  read.drawImage(picture, 0, 0, width, height);

  const { pixels, bounds } = inkOnly(read.getImageData(0, 0, width, height).data, width, height, colorMode);
  if (!bounds) throw new PictureError('signing.senderSignatureNoInk');

  // A little clear space kept around the ink, for the soft edge of a line to
  // fade out in rather than be cut straight.
  const margin = Math.round(Math.max(bounds.width, bounds.height) * 0.03) + 2;
  const left = Math.max(0, bounds.x - margin);
  const top = Math.max(0, bounds.y - margin);
  const inkWidth = Math.min(width, bounds.x + bounds.width + margin) - left;
  const inkHeight = Math.min(height, bounds.y + bounds.height + margin) - top;
  const cut = window.document.createElement('canvas');
  cut.width = inkWidth;
  cut.height = inkHeight;
  const cutting = cut.getContext('2d');
  if (!cutting) throw new Error('no 2d context');
  cutting.putImageData(new ImageData(pixels, width, height), -left, -top);

  const image = fitSignature(cut, inkWidth, inkHeight);
  if (!image) throw new PictureError('validation.signature.tooLarge');
  return image;
}

/** This device's storage, or null where the browser will not even hand it out. */
export function deviceStore(): SignatureStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

interface SavedSignaturesProps {
  /** The signatures kept on this device, as the list gives them. */
  saved: string[];
  /** Something is on the pad, a drawing or a picture, so there is something to add. */
  padFilled: boolean;
  busy: boolean;
  onPick: (image: string) => void;
  onAdd: () => void;
  onForget: (image: string) => void;
}

/**
 * 1.28 — the signatures kept on this device, listed: a thumbnail that picks
 * one, an × that takes it away, and "הוספה" to keep what is on the pad now. It
 * is what the dropdown opens, and a component of its own so that what it says
 * can be read without opening a menu.
 */
export function SavedSignaturesList({ saved, padFilled, busy, onPick, onAdd, onForget }: SavedSignaturesProps) {
  const { t } = useTranslation();
  const full = saved.length >= SAVED_SIGNATURES_MAX;

  return (
    <div className="p-2 space-y-2">
      {saved.length === 0 ? (
        <p className="px-1 py-1 text-sm text-muted-foreground">{t('signing.senderSignatureNoneKept')}</p>
      ) : (
        <ul className="space-y-1.5 max-h-[50vh] overflow-y-auto">
          {saved.map((image) => (
            <li key={image} className="flex items-center gap-1.5">
              <button
                type="button"
                aria-label={t('signing.senderSignatureUseKept')}
                title={t('signing.senderSignatureUseKept')}
                disabled={busy}
                onClick={() => onPick(image)}
                className="min-w-0 flex-1 rounded-lg border border-border bg-white p-1 transition-colors hover:border-primary disabled:opacity-50 disabled:pointer-events-none"
              >
                <img src={image} alt="" className="block h-12 w-full object-contain" />
              </button>
              <IconButton
                type="button"
                label={t('signing.senderSignatureForget')}
                className="disabled:opacity-50 disabled:pointer-events-none"
                disabled={busy}
                onClick={() => onForget(image)}
              >
                <X className="h-4 w-4" aria-hidden />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        size="sm"
        block
        icon={<Plus className="h-4 w-4" aria-hidden />}
        disabled={busy || !padFilled || full}
        onClick={onAdd}
      >
        {t('signing.senderSignatureAdd')}
      </Button>
      {full && (
        <p className="px-1 text-xs text-muted-foreground">
          {t('signing.senderSignatureListFull', { max: SAVED_SIGNATURES_MAX })}
        </p>
      )}
    </div>
  );
}

/** 1.28 — "חתימות שמורות ▾", at the pad's bottom end: the list above, opened under a button. */
function SavedSignatures(props: SavedSignaturesProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);

  return (
    <>
      <button
        ref={anchor}
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        disabled={props.busy}
        onClick={() => setOpen((was) => !was)}
        className="h-9 px-2.5 rounded-lg border border-border bg-card text-foreground text-xs font-medium inline-flex items-center gap-1 transition-colors hover:bg-muted disabled:opacity-50 disabled:pointer-events-none"
      >
        {t('signing.senderSignatureSavedList')}
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </button>
      <AnchoredMenu open={open} onClose={() => setOpen(false)} anchorRef={anchor} align="end" width={264}>
        <SavedSignaturesList {...props} />
      </AnchoredMenu>
    </>
  );
}

/**
 * The sender's signature: drawn, from a picture, or one of those kept on this
 * device. Saving hands back the signature already made small enough to keep;
 * closing the dialog hands back nothing, and nothing is placed or changed.
 *
 * The kept signatures belong to whoever is signed in (1.1, 17.09), so on an
 * office computer two people share, neither is offered the other's. Up to five
 * are kept (1.28), each added from the dropdown over the pad or by ticking the
 * box under it, and picking one puts it on the document and closes the dialog.
 * A device that will not keep one does not stop the signature going on the
 * document.
 */
export function SenderSignatureDialog({
  onSave,
  onClose,
}: {
  onSave: (image: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const owner = useSession().session?.user.id;
  const [saved, setSaved] = useState(() => (owner ? savedSignatures(deviceStore(), owner) : []));
  const [drawn, setDrawn] = useState<string | null>(null);
  const [stampMode, setStampMode] = useState<'original' | 'black-white'>('original');
  const stampFile = useRef<File | undefined>(undefined);
  const [picture, setPicture] = useState<string | null>(null);
  const [stamp, setStamp] = useState<string | null>(null);
  const [combinedPreview, setCombinedPreview] = useState<string | null>(null);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pictureInput = useRef<HTMLInputElement>(null);
  const stampInput = useRef<HTMLInputElement>(null);

  /** What is on the pad, made small enough to keep; null for a drawing that stays huge, or nothing on it. */
  const padImage = async () => senderSigningImage(picture ?? drawn, stamp);

  useEffect(() => {
    let current = true;
    setCombinedPreview(null);
    if (stamp && (picture || drawn)) {
      void senderSigningImage(picture ?? drawn, stamp).then((image) => {
        if (current) setCombinedPreview(image);
      }).catch(() => { if (current) setError('errors.unknown'); });
    }
    return () => { current = false; };
  }, [drawn, picture, stamp]);

  const save = async () => {
    if (busy || (!picture && !drawn && !stamp)) return;
    setBusy(true);
    setError(null);
    try {
      const image = await padImage();
      if (image) {
        if (remember && owner && !rememberSignature(deviceStore(), owner, image)) {
          toast.error(t('signing.senderSignatureNotKept'));
        }
        onSave(image);
        return;
      }
      // Keep the server/storage cap even if no bounded encoding fits.
      setError('validation.signature.tooLarge');
    } catch {
      setError('errors.unknown');
    }
    setBusy(false);
  };

  /** "הוספה": what is on the pad joins the list, and the dialog stays open. */
  const add = async () => {
    if (!owner || busy || (!picture && !drawn && !stamp)) return;
    setBusy(true);
    setError(null);
    try {
      const image = await padImage();
      if (!image) setError('validation.signature.tooLarge');
      else if (rememberSignature(deviceStore(), owner, image)) setSaved(savedSignatures(deviceStore(), owner));
      else setError('signing.senderSignatureKeepFailed');
    } catch {
      setError('errors.unknown');
    }
    setBusy(false);
  };

  const upload = async (file: File | undefined) => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      setPicture(await signatureFromPicture(file));
      // The pad goes away for the picture, and what was drawn on it goes with it.
      setDrawn(null);
    } catch (failure) {
      setError(failure instanceof PictureError ? failure.message : 'errors.unknown');
    }
    setBusy(false);
  };

  const uploadStamp = async (file: File | undefined, mode = stampMode) => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    try {
      setStamp(await signatureFromPicture(file, mode));
      stampFile.current = file;
    } catch (failure) {
      setError(failure instanceof PictureError ? failure.message : 'errors.unknown');
    }
    setBusy(false);
  };

  const pickSaved = (image: string) => {
    if (busy) return;
    setPicture(image);
    setDrawn(null);
  };

  const forget = (image: string) => {
    if (!owner) return;
    if (forgetSignature(deviceStore(), owner, image)) setSaved(savedSignatures(deviceStore(), owner));
    else setError('signing.senderSignatureForgetFailed');
  };

  return (
    <Modal
      open
      title={t('signing.senderSignatureTitle')}
      size="wide"
      layer={70}
      onClose={onClose}
      footer={
        <Button type="button" variant="primary" disabled={!drawn && !picture && !stamp} loading={busy} onClick={() => void save()}>
          {t('signing.signatureConfirm')}
        </Button>
      }
    >
      <p className="text-sm text-muted-foreground">{t('signing.senderSignatureHint')}</p>
      {/* The frame: the pad, or the picture put in its place, with the saved
          signatures at its bottom end. The pad's own icons are at its start. */}
      <div className="relative">
        {picture ? (
          <div className="relative aspect-[2/1] w-full rounded-md bg-white">
            {stamp && <img src={stamp} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-contain" />}
            <img src={picture} alt={t('signing.senderSignaturePicture')} className="relative block h-full w-full rounded-md object-contain" />
          </div>
        ) : (
          <SignaturePad
            onChange={(image) => {
              setDrawn(image);
              setError(null);
            }}
            disabled={busy}
            clearLabel={t('publicSign.clear')}
            signHere={t('signing.senderSignatureSignHere')}
            backdrop={stamp}
            iconButtons
            className="signature-pad !h-auto aspect-[2/1] !border-0"
          />
        )}
        {owner && (
          <div className="flex justify-end mt-3">
            <SavedSignatures
              saved={saved}
              padFilled={Boolean(drawn || picture)}
              busy={busy}
              onPick={(image) => void pickSaved(image)}
              onAdd={() => void add()}
              onForget={forget}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 sm:flex sm:gap-3">
        <Button type="button" size="sm" className="min-w-0 gap-1.5 px-2" disabled={busy} icon={<Stamp className="h-4 w-4 shrink-0" aria-hidden />} onClick={() => stampInput.current?.click()}>
          {t('signing.senderStampUpload')}
        </Button>
      </div>
      {stamp && (
        <div role="group" aria-label={t('signing.stampColorMode')} className="flex flex-wrap gap-2">
          {(['original', 'black-white'] as const).map(mode => <Button key={mode} type="button" size="sm" disabled={busy} variant={stampMode === mode ? 'primary' : 'secondary'} aria-pressed={stampMode === mode} onClick={() => { setStampMode(mode); if (stampFile.current) void uploadStamp(stampFile.current, mode); }}>
            {t(mode === 'original' ? 'signing.stampOriginal' : 'signing.stampBlackWhite')}
          </Button>)}
        </div>
      )}
      {stamp && (
        <div className="flex items-center gap-3">
          <img src={stamp} alt={t('signing.senderStampPicture')} className="h-16 max-w-32 object-contain rounded-md border border-border bg-white" />
          <IconButton type="button" label={t('signing.senderStampRemove')} disabled={busy} onClick={() => { setStamp(null); stampFile.current = undefined; }}>
            <X className="h-4 w-4" />
          </IconButton>
        </div>
      )}
      {combinedPreview && <img src={combinedPreview} alt={t('signing.senderSignatureCombinedPreview')} className="block w-full h-24 rounded-md border border-border bg-white object-contain" />}
      {/* Return to drawing, and optionally keep the current signature. */}
      {(picture || (owner && drawn)) && <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        {picture && (
          <Button type="button" size="sm" disabled={busy} onClick={() => setPicture(null)}>
            {t('signing.senderSignatureDraw')}
          </Button>
        )}
        {owner && (drawn || picture) && (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={remember}
              onChange={(event) => setRemember(event.target.checked)}
              className="h-5 w-5 shrink-0 accent-primary"
            />
            {t('signing.senderSignatureRemember')}
          </label>
        )}
      </div>}
      <input
        ref={pictureInput}
        hidden
        type="file"
        accept="image/png,image/jpeg"
        onChange={(event) => {
          void upload(event.target.files?.[0]);
          // Picking the same file again is still a change.
          event.target.value = '';
        }}
      />
      <input
        ref={stampInput}
        hidden
        type="file"
        accept="image/png,image/jpeg"
        onChange={(event) => {
          void uploadStamp(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
      {error && <ErrorNotice code={error} />}
    </Modal>
  );
}
